import React, { useState, useEffect } from "react";
import {
  ChartBarIcon,
  PhoneIcon,
  CogIcon,
  QueueListIcon,
  UsersIcon,
  SignalIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  WifiIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import {
  fetchPhoneroPresenceCodes,
  updatePhoneroUserPresence,
  fetchPhoneroUserPresence,
  fetchPhoneroUserPresenceState,
} from "../../../api/company";
import { useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import CallsList from "../../../components/dashboard/phonero/CallsList";
import CallNotification from "../../../components/dashboard/phonero/CallNotification";
import QueuesList from "../../../components/dashboard/phonero/QueuesList";
import PersonsManager from "../../../components/dashboard/phonero/PersonsManager";
import WebSocketManager from "../../../components/dashboard/phonero/WebSocketManager";
import PhoneroSettings from "../../../components/dashboard/phonero/PhoneroSettings";
import RoleOnly from "../../../routes/roleOnly";

const ControlPanel = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("overview");

  // Phone status state
  const [phoneInfo, setPhoneInfo] = useState({});
  const [presenceCodes, setPresenceCodes] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [syncStatus, setSyncStatus] = useState({
    connected: false,
    message: "",
  });

  const currentUser = useSelector((state) => state.session.currentUser);

  // Create a minimal company object with just the properties we need
  // This avoids the need to fetch company data that regular users might not have access to
  const company = {
    id: currentUser?.company_id || 1, // Default to 1 if not available
    phonero_username: null, // We'll get this from phone status if needed
  };

  const tabs = [
    {
      id: "overview",
      name: t("overview"),
      icon: ChartBarIcon,
      role: "phone",
    },
    {
      id: "queues",
      name: t("queues"),
      icon: QueueListIcon,
      role: "phone_settings",
    },
    { id: "calls", name: t("calls"), icon: PhoneIcon, role: "phone" },
    {
      id: "persons",
      name: t("persons"),
      icon: UsersIcon,
      role: "phone_settings",
    },
    {
      id: "settings",
      name: t("settings"),
      icon: CogIcon,
      role: "phone_settings",
    },
  ];

  // Fetch phone information and presence codes
  useEffect(() => {
    async function fetchPhoneData() {
      if (!currentUser) return;

      try {
        // Always try to fetch the current user's presence state
        const presenceResponse = await fetchPhoneroUserPresenceState();

        // Also fetch presence codes for the change status functionality
        const codesResponse = await fetchPhoneroPresenceCodes();

        if (codesResponse.data?.success) {
          setPresenceCodes(codesResponse.data.codes || []);
        }

        if (presenceResponse.data?.success) {
          const presence = presenceResponse.data.presence?.result[0];

          // Update sync status based on successful response
          setSyncStatus({
            connected: true,
            message: presence
              ? `${t("connected.to.phonero")} (${presence.presenceDescription})`
              : t("connected.to.phonero"),
          });

          if (presence) {
            setPhoneInfo({
              personId: presence.personId,
              username: presence.username,
              presenceId: presence.presenceId,
              presenceDescription: presence.presenceDescription,
              presenceText: presence.presenceText,
              mobile: presence.mobile,
              connected: true,
            });
          } else {
            setPhoneInfo({
              connected: true,
              error: t("presence.not.available"),
            });
          }
        } else {
          // Backend returned an error (user not connected, no phonero config, etc.)
          setSyncStatus({
            connected: false,
            message:
              presenceResponse.data?.message || t("not.connected.to.phonero"),
          });

          setPhoneInfo({
            error:
              presenceResponse.data?.message || t("presence.failed.to.load"),
            connected: false,
          });
        }
      } catch (error) {
        console.error("Phone data fetch error:", error);
        setPhoneInfo({
          error: t("presence.failed.to.load"),
          connected: false,
        });
      }
    }

    fetchPhoneData();
  }, [currentUser, t]);

  const handlePresenceUpdate = async (newPresenceId) => {
    if (!phoneInfo.personId && !phoneInfo.username) {
      toast.error(t("presence.cannot.update"));
      return;
    }

    setIsUpdating(true);
    try {
      const identifier = phoneInfo.personId || phoneInfo.username;
      const response = await updatePhoneroUserPresence(identifier, {
        presence_id: newPresenceId,
        presence_text: "",
        presence_expiry: null,
      });

      if (response.data?.success) {
        toast.success(t("presence.updated.successfully"));

        // Refresh phone data using the direct API
        const presenceResponse = await fetchPhoneroUserPresenceState();
        if (presenceResponse.data?.success) {
          const presence = presenceResponse.data.presence?.result[0];

          // Update sync status
          setSyncStatus({
            connected: true,
            message: presence
              ? `${t("connected.to.phonero")} (${presence.presenceDescription})`
              : t("connected.to.phonero"),
          });

          if (presence) {
            setPhoneInfo({
              personId: presence.personId,
              username: presence.username,
              presenceId: presence.presenceId,
              presenceDescription: presence.presenceDescription,
              presenceText: presence.presenceText,
              mobile: presence.mobile,
              connected: true,
            });
          }
        }
      } else {
        toast.error(t("presence.failed.to.update"));
      }
    } catch (error) {
      console.error("Presence update error:", error);
      toast.error(t("presence.failed.to.update"));
    } finally {
      setIsUpdating(false);
    }
  };

  const getPresenceStatusColor = (presenceId) => {
    switch (presenceId) {
      case 1:
        return "text-green-600";
      case 2:
        return "text-yellow-600";
      case 3:
        return "text-red-600";
      default:
        return "text-gray-600";
    }
  };

  const getPresenceStatusIcon = (presenceId) => {
    switch (presenceId) {
      case 1:
        return <SignalIcon className="h-5 w-5 text-green-600" />;
      case 2:
        return <SignalIcon className="h-5 w-5 text-yellow-600" />;
      case 3:
        return <SignalIcon className="h-5 w-5 text-red-600" />;
      default:
        return <SignalIcon className="h-5 w-5 text-gray-600" />;
    }
  };

  const renderTabContent = () => {
    // Remove loading check since we don't fetch company data anymore
    // Just check if currentUser exists
    if (!currentUser) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-500">{t("loading")}</p>
          </div>
        </div>
      );
    }

    switch (activeTab) {
      case "overview":
        return (
          <RoleOnly role="phone" action="read">
            <div className="space-y-6">
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {t("control.panel.overview.title")}
                  </h3>
                  <p className="mt-1 max-w-2xl text-sm text-gray-500">
                    {t("control.panel.overview.description")}
                  </p>
                </div>
              </div>

              {/* Phone Status Section */}
              <RoleOnly role="phone" action="read">
                <div className="bg-white overflow-hidden shadow rounded-lg">
                  <div className="px-4 py-5 sm:p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        {t("current.phone.status")}
                      </h3>
                      <div className="flex items-center gap-2">
                        {syncStatus.connected ? (
                          <CheckCircleIcon className="h-5 w-5 text-green-600" />
                        ) : (
                          <ExclamationCircleIcon className="h-5 w-5 text-red-600" />
                        )}
                        <span className="text-sm text-gray-500">
                          {syncStatus.message}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4">
                      <div
                        className={`flex-shrink-0 p-3 rounded-full ${
                          phoneInfo.connected
                            ? "bg-green-100 text-green-600"
                            : "bg-red-100 text-red-600"
                        }`}
                      >
                        <PhoneIcon className="h-8 w-8" />
                      </div>
                      <div className="flex-1">
                        <div className="text-2xl font-bold text-gray-900">
                          {phoneInfo.error && !phoneInfo.connected
                            ? phoneInfo.error
                            : phoneInfo.mobile ||
                              currentUser?.mobile_work ||
                              currentUser?.mobile ||
                              t("connected.to.phonero")}
                        </div>
                        {phoneInfo.connected && phoneInfo.presenceId && (
                          <div className="flex items-center gap-2 mt-2">
                            {getPresenceStatusIcon(phoneInfo.presenceId)}
                            <span
                              className={`font-medium ${getPresenceStatusColor(
                                phoneInfo.presenceId
                              )}`}
                            >
                              {t(
                                phoneInfo.presenceDescription
                                  ?.charAt(0)
                                  .toUpperCase() +
                                  phoneInfo.presenceDescription
                                    ?.slice(1)
                                    .toLowerCase()
                              ) || t("presence.unknown")}
                            </span>
                            {phoneInfo.presenceText && (
                              <span className="text-sm text-gray-500">
                                - {t(phoneInfo.presenceText)}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Presence Status Change */}
                    {phoneInfo.connected &&
                      presenceCodes.length > 0 &&
                      phoneInfo.presenceId && (
                        <div className="mt-6">
                          <h4 className="text-sm font-medium text-gray-700 mb-3">
                            {t("change.status")}
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {presenceCodes.map((code) => (
                              <button
                                key={code.id}
                                onClick={() => handlePresenceUpdate(code.id)}
                                disabled={
                                  isUpdating || code.id === phoneInfo.presenceId
                                }
                                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                  code.id === phoneInfo.presenceId
                                    ? "bg-blue-100 text-blue-800 border-2 border-blue-300"
                                    : "bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-gray-200"
                                } ${
                                  isUpdating
                                    ? "opacity-50 cursor-not-allowed"
                                    : ""
                                }`}
                              >
                                {t(code.description)}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                    {!phoneInfo.connected && !phoneInfo.error && (
                      <div className="mt-4 text-center text-gray-500">
                        <WifiIcon className="h-8 w-8 mx-auto mb-2" />
                        <p>{t("not.connected.to.phone.system")}</p>
                      </div>
                    )}
                  </div>
                </div>
              </RoleOnly>

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {/* Phonero Status Card */}
                <RoleOnly role="phone" action="read">
                  <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <PhoneIcon className="h-6 w-6 text-green-600" />
                        </div>
                        <div className="ml-5 w-0 flex-1">
                          <dl>
                            <dt className="text-sm font-medium text-gray-500 truncate">
                              {t("phonero.status")}
                            </dt>
                            <dd className="text-lg font-medium text-gray-900">
                              {phoneInfo.connected
                                ? t("active")
                                : t("not.configured")}
                            </dd>
                          </dl>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-50 px-5 py-3">
                      <div className="text-sm">
                        <button
                          onClick={() => setActiveTab("queues")}
                          className="font-medium text-indigo-600 hover:text-indigo-500"
                        >
                          {t("view.details")}
                        </button>
                      </div>
                    </div>
                  </div>
                </RoleOnly>

                {/* System Status Card */}
                <div className="bg-white overflow-hidden shadow rounded-lg">
                  <div className="p-5">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <ChartBarIcon className="h-6 w-6 text-blue-600" />
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <dl>
                          <dt className="text-sm font-medium text-gray-500 truncate">
                            {t("system.status")}
                          </dt>
                          <dd className="text-lg font-medium text-gray-900">
                            {t("all.systems.operational")}
                          </dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-5 py-3">
                    <div className="text-sm">
                      <span className="font-medium text-green-600">
                        {t("last.updated")}:{" "}
                        {new Date().toLocaleTimeString("en-US")}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Quick Actions Card */}
                <RoleOnly role="phone_settings" action="settings">
                  <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <CogIcon className="h-6 w-6 text-gray-600" />
                        </div>
                        <div className="ml-5 w-0 flex-1">
                          <dl>
                            <dt className="text-sm font-medium text-gray-500 truncate">
                              {t("quick.actions")}
                            </dt>
                            <dd className="text-lg font-medium text-gray-900">
                              {t("settings")}
                            </dd>
                          </dl>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-50 px-5 py-3">
                      <div className="text-sm">
                        <button
                          onClick={() => setActiveTab("settings")}
                          className="font-medium text-indigo-600 hover:text-indigo-500"
                        >
                          {t("configure")}
                        </button>
                      </div>
                    </div>
                  </div>
                </RoleOnly>
              </div>
            </div>
          </RoleOnly>
        );

      case "queues":
        return (
          <RoleOnly role="phone_settings" action="read">
            <div className="space-y-6">
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {t("phonero.queues")}
                  </h3>
                  <p className="mt-1 max-w-2xl text-sm text-gray-500">
                    {t("monitor.manage.queues")}
                  </p>
                </div>
              </div>

              <QueuesList companyId={company?.id} />
            </div>
          </RoleOnly>
        );

      case "calls":
        return (
          <RoleOnly role="phone" action="read">
            <div className="space-y-6">
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {t("call.log")}
                  </h3>
                  <p className="mt-1 max-w-2xl text-sm text-gray-500">
                    {t("view.call.history")}
                  </p>
                </div>
              </div>

              <CallsList companyId={company?.id} />
            </div>
          </RoleOnly>
        );

      case "persons":
        return (
          <RoleOnly role="phone" action="read">
            <div className="space-y-6">
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {t("phonero.persons")}
                  </h3>
                  <p className="mt-1 max-w-2xl text-sm text-gray-500">
                    {t("manage.persons.phone")}
                  </p>
                </div>
              </div>

              <PersonsManager companyId={company?.id} />
            </div>
          </RoleOnly>
        );

      case "settings":
        return (
          <RoleOnly role="phone_settings" action="settings">
            <div className="space-y-6">
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {t("control.panel.settings")}
                  </h3>
                  <p className="mt-1 max-w-2xl text-sm text-gray-500">
                    {t("configure.control.panel")}
                  </p>
                </div>
              </div>

              <div className="bg-white shadow overflow-hidden sm:rounded-md">
                <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {t("integrations")}
                  </h3>
                  <p className="mt-1 max-w-2xl text-sm text-gray-500">
                    {t("manage.all.integrations")}
                  </p>
                </div>

                <ul className="divide-y divide-gray-200">
                  <RoleOnly role="phone_settings" action="settings">
                    <li className="px-4 py-4 hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <PhoneIcon className="h-5 w-5 text-green-600" />
                          <div className="ml-4">
                            <p className="text-sm font-medium text-gray-900">
                              Phonero
                            </p>
                            <p className="text-sm text-gray-500">
                              {t("phone.integration")}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              phoneInfo.connected
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {phoneInfo.connected
                              ? t("active")
                              : t("not.configured")}
                          </span>
                          <button
                            onClick={() => setActiveTab("queues")}
                            className="ml-4 text-sm font-medium text-indigo-600 hover:text-indigo-500"
                          >
                            {t("view.details")}
                          </button>
                        </div>
                      </div>
                    </li>
                  </RoleOnly>
                </ul>
              </div>

              {/* Phonero Settings */}
              <PhoneroSettings companyId={company.id} />

              {/* WebSocket Manager for real-time updates */}
              <RoleOnly role="phone" action="read">
                <WebSocketManager companyId={company.id} />
              </RoleOnly>
            </div>
          </RoleOnly>
        );

      default:
        return null;
    }
  };

  // Remove the loading check since we don't fetch company data anymore
  if (!currentUser) {
    return (
      <div className="min-h-screen bg-gray-100 flex justify-center items-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="px-4 py-6 sm:px-0">
          <h1 className="text-3xl font-bold text-gray-900">
            {t("control.panel")}
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            {t("control.panel.overview")}
          </p>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <RoleOnly key={tab.id} role={tab.role} action="read">
                <button
                  onClick={() => setActiveTab(tab.id)}
                  className={`${
                    activeTab === tab.id
                      ? "border-indigo-500 text-indigo-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
                >
                  <tab.icon className="h-5 w-5 mr-2" />
                  {tab.name}
                </button>
              </RoleOnly>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="px-4 sm:px-0">{renderTabContent()}</div>
      </div>

      {/* Real-time call notifications */}
      {/* <RoleOnly role="phone" action="read">
        <CallNotification companyId={company.id} />
      </RoleOnly> */}
    </div>
  );
};

export default ControlPanel;
